"use client";

import { useCallback, useMemo, useState } from "react";
import { ChevronLeft, Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/shadcn-components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shadcn-components/ui/tabs";
import { ScrollArea } from "@/shadcn-components/ui/scroll-area";
import { Switch } from "@/shadcn-components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/shadcn-components/ui/alert-dialog";
import DrawingsAddScreen from "./DrawingsAddScreen";
import DrawingConfigFactory from "./DrawingConfigFactory";
import { useDrawingsStore } from "@/lib/chart-drawings/store";
import type { DrawingCatalogEntry, DrawingInstance } from "@/lib/chart-drawings/types";
import { getDrawingName } from "@/lib/chart-drawings/registry";
import { getObjectIcon } from "@/lib/chart-drawings/hooks";
import { useChartStore3 } from "@/vybe-trader-client/chart/stores/useChartStore3";
import { useChartPanelId } from "@/components/chart/ChartPanelContext";

interface DrawingsScreenProps {
  onBack: () => void;
  setSelectedDrawingType: (type: string) => void;
  isDesktop?: boolean;
}

type ScreenView = "list" | "config";

export default function DrawingsScreen({
  onBack,
  setSelectedDrawingType,
  isDesktop = false,
}: DrawingsScreenProps) {
  const chartPanelId = useChartPanelId();
  const selectedSymbol = useChartStore3(
    (state) => state.slots[chartPanelId].selectedSymbol
  );

  const objects = useDrawingsStore((state) => state.objects);
  const updateDrawingSettings = useDrawingsStore(
    (state) => state.updateDrawingSettings
  );
  const deleteDrawingById = useDrawingsStore((state) => state.deleteDrawingById);
  const deleteDrawingsForSymbol = useDrawingsStore(
    (state) => state.deleteDrawingsForSymbol
  );

  const [view, setView] = useState<ScreenView>("list");
  const [activeTab, setActiveTab] = useState<"active" | "add">("active");
  const [configuringDrawing, setConfiguringDrawing] =
    useState<DrawingInstance | null>(null);

  const symbolDrawings = useMemo(
    () => objects.filter((obj) => obj.symbolKey === selectedSymbol?.currency),
    [objects, selectedSymbol?.currency]
  );

  const handleSelectCatalogEntry = useCallback(
    (entry: DrawingCatalogEntry) => {
      setSelectedDrawingType(entry.drawingId);
      onBack();
    },
    [setSelectedDrawingType, onBack]
  );

  const handleEditDrawing = (drawing: DrawingInstance) => {
    setConfiguringDrawing({ ...drawing });
    setView("config");
  };

  const handleSaveDrawing = (updated: DrawingInstance) => {
    // DR-05: pass only whitelisted instance-level fields, not the whole edited
    // instance — otherwise top-level junk (id already stripped, plus any stray
    // keys) would be spread onto the stored object.
    updateDrawingSettings(updated.id, {
      name: updated.name,
      visible: updated.visible,
      settings: updated.settings,
    });
    setConfiguringDrawing(null);
    setView("list");
    setActiveTab("active");
  };

  const handleDeleteAll = () => {
    if (selectedSymbol?.currency) {
      deleteDrawingsForSymbol(selectedSymbol.currency);
    }
  };

  if (view === "config" && configuringDrawing) {
    return (
      <DrawingConfigFactory
        drawing={configuringDrawing}
        onBack={() => {
          setConfiguringDrawing(null);
          setView("list");
        }}
        onSave={handleSaveDrawing}
      />
    );
  }

  const headerH = isDesktop ? "h-10 px-3" : "h-14 px-4";

  return (
    <div className="h-full flex flex-col min-h-0 bg-background text-foreground">
      <div
        className={`flex items-center justify-between border-b border-border shrink-0 ${headerH}`}
      >
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          className={isDesktop ? "h-7 w-7" : "h-10 w-10"}
        >
          <ChevronLeft className={isDesktop ? "h-4 w-4" : "h-5 w-5"} />
        </Button>
        <h1 className={`font-medium ${isDesktop ? "text-sm" : "text-lg"}`}>
          Drawings
        </h1>
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className={`text-primary ${isDesktop ? "text-xs h-7" : ""}`}
        >
          Done
        </Button>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as "active" | "add")}
        className="flex flex-col flex-1 min-h-0"
      >
        <TabsList className="w-full justify-start rounded-none border-b border-border bg-transparent h-9 px-2 shrink-0">
          <TabsTrigger value="active" className="text-xs data-[state=active]:bg-muted">
            Active
          </TabsTrigger>
          <TabsTrigger value="add" className="text-xs data-[state=active]:bg-muted">
            Add
          </TabsTrigger>
        </TabsList>

        <TabsContent
          value="active"
          className="flex-1 min-h-0 mt-0 data-[state=inactive]:hidden"
        >
          <ScrollArea className="h-full">
            <div className="p-3 space-y-3">
              {symbolDrawings.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8 rounded-lg border border-dashed border-border">
                  No drawings on {selectedSymbol?.currency ?? "this symbol"}
                </p>
              ) : (
                <>
                  <div className="flex items-center justify-between px-1">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {selectedSymbol?.currency}
                    </span>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete all drawings</AlertDialogTitle>
                          <AlertDialogDescription>
                            Remove all drawings for {selectedSymbol?.currency}?
                            This cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={handleDeleteAll}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Delete all
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                  <ul className="space-y-2">
                    {symbolDrawings.map((drawing) => (
                      <DrawingRow
                        key={drawing.id}
                        drawing={drawing}
                        onEdit={() => handleEditDrawing(drawing)}
                        onDelete={() => deleteDrawingById(drawing.id)}
                        onToggleVisible={(visible) =>
                          updateDrawingSettings(drawing.id, { visible })
                        }
                      />
                    ))}
                  </ul>
                </>
              )}
            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent
          value="add"
          className="flex-1 min-h-0 mt-0 data-[state=inactive]:hidden"
        >
          <DrawingsAddScreen
            onBack={() => setActiveTab("active")}
            onSelectDrawing={handleSelectCatalogEntry}
            isDesktop={isDesktop}
          />
        </TabsContent>
      </Tabs>

      {activeTab === "active" && (
        <div className="p-3 border-t border-border shrink-0">
          <Button
            className="w-full gap-2"
            size="sm"
            onClick={() => setActiveTab("add")}
          >
            <Plus className="h-4 w-4" />
            Add drawing
          </Button>
        </div>
      )}
    </div>
  );
}

function DrawingRow({
  drawing,
  onEdit,
  onDelete,
  onToggleVisible,
}: {
  drawing: DrawingInstance;
  onEdit: () => void;
  onDelete: () => void;
  onToggleVisible: (visible: boolean) => void;
}) {
  const visible = drawing.visible !== false;

  return (
    <li className="flex items-center gap-2 rounded-lg border border-border bg-card/40 px-3 py-2">
      <Switch
        checked={visible}
        onCheckedChange={onToggleVisible}
        aria-label={visible ? "Hide drawing" : "Show drawing"}
        className="shrink-0"
      />
      <span className="w-6 h-6 flex items-center justify-center shrink-0">
        {getObjectIcon(drawing.type)}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">
          {drawing.name || getDrawingName(drawing.type)}
        </div>
        <div className="text-xs text-muted-foreground truncate">
          {getDrawingName(drawing.type)}
        </div>
      </div>
      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={onEdit}>
        <Pencil className="h-3.5 w-3.5" />
      </Button>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0 text-destructive hover:text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove drawing</AlertDialogTitle>
            <AlertDialogDescription>
              Remove &quot;{drawing.name || getDrawingName(drawing.type)}&quot; from
              the chart?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </li>
  );
}
